/* ComplimentaryPersonnel.jsx - Manage Ödenmez personnel directory */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, Edit2, Search, Users } from 'lucide-react';

export default function ComplimentaryPersonnel() {
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ full_name: '', title: '', department: '' });

    useEffect(() => { fetch(); }, []);

    const fetch = async () => {
        setLoading(true);
        try {
            const params = search ? { q: search } : {};
            const res = await api.get('/complimentary/personnel', { params });
            setList(res.data || []);
        } catch {
            toast.error('Personel yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ full_name: '', title: '', department: '' });
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({ full_name: p.full_name, title: p.title || '', department: p.department || '' });
        setDialogOpen(true);
    };

    const save = async () => {
        if (!form.full_name.trim()) {
            toast.error('Ad-Soyad gerekli');
            return;
        }
        try {
            if (editing) {
                await api.put(`/complimentary/personnel/${editing.id}`, form);
                toast.success('Güncellendi');
            } else {
                await api.post('/complimentary/personnel', form);
                toast.success('Eklendi');
            }
            setDialogOpen(false);
            fetch();
        } catch {
            toast.error('Kaydetme başarısız');
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Bu personeli silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/complimentary/personnel/${id}`);
            toast.success('Silindi');
            fetch();
        } catch {
            toast.error('Silme başarısız');
        }
    };

    const filtered = list.filter(p => !search || p.full_name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="space-y-4" data-testid="comp-personnel-page">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigate('/odenmez')}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Geri
                    </Button>
                    <Users className="w-6 h-6 text-slate-600" />
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Ödenmez Personel Rehberi</h1>
                </div>
                <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-personnel-btn">
                    <Plus className="w-4 h-4 mr-2" /> Yeni Personel
                </Button>
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Personel ({filtered.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="relative max-w-md">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ad-soyad ile ara..." className="pl-8 h-10" data-testid="personnel-search" />
                    </div>

                    <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-100 border-b">
                                <tr>
                                    <th className="text-left p-3 font-medium text-slate-600">Ad Soyad</th>
                                    <th className="text-left p-3 font-medium text-slate-600">Görevi</th>
                                    <th className="text-left p-3 font-medium text-slate-600">Departman</th>
                                    <th className="text-center p-3 font-medium text-slate-600">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="4" className="p-6 text-center text-slate-400">Yükleniyor...</td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr><td colSpan="4" className="p-6 text-center text-slate-400">Personel yok</td></tr>
                                ) : filtered.map(p => (
                                    <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`personnel-row-${p.id}`}>
                                        <td className="p-3 font-medium">{p.full_name}</td>
                                        <td className="p-3 text-slate-600">{p.title || '—'}</td>
                                        <td className="p-3 text-slate-600">{p.department || '—'}</td>
                                        <td className="p-3 text-center">
                                            <div className="flex justify-center gap-1">
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Edit2 className="w-4 h-4" /></Button>
                                                <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Personel Düzenle' : 'Yeni Personel'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Ad Soyad *</Label>
                            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Örn: Abdullah Coşkun" data-testid="form-personnel-name" />
                        </div>
                        <div>
                            <Label>Görevi</Label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Örn: Genel Müdür" data-testid="form-personnel-title" />
                        </div>
                        <div>
                            <Label>Departman</Label>
                            <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Örn: Yönetim" data-testid="form-personnel-department" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
                        <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="form-personnel-save">{editing ? 'Güncelle' : 'Ekle'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
